/**
 * Funcionário — detalhe por ABAS (U3): Dados (edição básica + chaves externas
 * editáveis — matrícula Domínio/ID Convenia — e UUID Convenia read-only, correção
 * E10b), Contrato (histórico em Timeline + nova vigência em modal guiado com diff
 * antes→depois e erro de retroatividade em linguagem simples + cartão de
 * desligamento espelhado do Convenia — E10e), Histórico salarial (espelho
 * Convenia — E10e), Benefícios, Dependentes, Banco de horas (componente
 * existente reutilizado), Documentos (+ Arquivos do Convenia — E10e),
 * Bancário, Emergência & ASO e Anotações & campos (E10b — somente leitura, a
 * fonte é o sync Convenia).
 */
import { useEffect, useState } from "react";
import { Avatar, Badge, Button, Card, Checkbox, FileButton, Group, Modal, Select, Switch, Tabs, Text, TextInput, Timeline, Title } from "@mantine/core";
import { IconCamera, IconPlus, IconTrash } from "@tabler/icons-react";
import { api, ErroApi } from "../api";
import { fmtMoeda, fmtData, fmtCompetencia } from "../format";
import { CompetenciaField } from "../componentes/entradas-mantine";
import { MoedaField, DataField } from "../componentes/entradas-mantine";
import { ErroApiAlert, OkAlert, AvisosAlert, avisosDe } from "../componentes/notificacoes";
import { notificarOk, notificarErroApi } from "../componentes/notificacoes";
import { AvatarPessoa, invalidarCacheFoto } from "../componentes/listas";
import { BancoHorasPage } from "./BancoHorasPage";
import { DependentesTab } from "./funcionario/DependentesTab";
import { BeneficiosTab } from "./funcionario/BeneficiosTab";
import { DocumentosTab } from "./funcionario/DocumentosTab";
import { BancarioTab } from "./funcionario/BancarioTab";
import { EmergenciaAsoTab } from "./funcionario/EmergenciaAsoTab";
import { FeriasAfastamentosTab } from "./funcionario/FeriasAfastamentosTab";
import { AnotacoesCamposTab } from "./funcionario/AnotacoesCamposTab";
import { HistoricoSalarialTab } from "./funcionario/HistoricoSalarialTab";
import { MediasTab } from "./funcionario/MediasTab";
import { DesligamentoCard } from "./funcionario/DesligamentoCard";

interface Contrato {
  id: string;
  vigenciaInicio: number;
  vigenciaFim: number | null;
  salarioBaseCentavos: number;
  bonusFixoMensalCentavos: number;
  cargo: string | null;
  cbo: string | null;
  departamentoDominio: number | null;
  situacao: string;
  admissao: string;
  /** 05/09/2026 — isento de ponto (cargo de confiança, art. 62 CLT); null/false = bate ponto. */
  isentoPonto?: boolean | null;
  jornada: { id: string; descricao: string; divisorHorasMes: number };
}
interface Beneficio {
  id: string;
  tipo: string;
  vigenciaInicio: number;
  vigenciaFim: number | null;
  vtItens: { tarifaCentavos: number; quantidade: number }[];
  planoBeneficiarios: { descricao: string; mensalidadeCentavos: number; percentualFuncionario: number; percentualEmpresa: number }[];
}
interface Dependente {
  id: string;
  quantidade: number;
  vigenciaInicio: number;
  vigenciaFim: number | null;
}
interface Jornada {
  id: string;
  descricao: string;
  divisorHorasMes: number;
}
/** Proposta da 1ª vigência derivada do Convenia (GET .../proposta-primeira-vigencia). */
interface PropostaPrimeiraVigencia {
  salarioBaseCentavos: number | null;
  cargo: string | null;
  departamentoDominio: number | null;
  jornadaId: string | null;
  admissao: string | null;
  motivoSalarial: string | null;
  dateFromSalarial: string | null;
  alertas: string[];
  bloqueios: string[];
}
interface Detalhe {
  id: string;
  nome: string;
  matriculaDominio: number | null;
  idConvenia: number | null;
  conveniaUuid: string | null;
  cpf: string | null;
  tipoVinculo: string;
  ativo: boolean;
  /** 26/08/2026 — flag efetivo do "Permitir direito a férias" (pedido do dono). */
  direitoFerias?: boolean;
  observacao: string | null;
  contratos: Contrato[];
  dependentes: Dependente[];
  beneficios: Beneficio[];
}

/** (removido no P2 — o 409 de retroatividade deixou de existir: cadastro
 *  histórico é permitido com aviso; os avisos aparecem via AvisosAlert) */

export function FuncionarioPage({ id }: { id: string }) {
  const [f, setF] = useState<Detalhe | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [ok, setOk] = useState("");
  // edição básica (aba Dados)
  const [editNome, setEditNome] = useState("");
  const [editCpf, setEditCpf] = useState("");
  const [editAtivo, setEditAtivo] = useState(true);
  const [editObs, setEditObs] = useState("");
  // 26/08/2026 — "Permitir direito a férias" (Switch do cadastro; default deriva do vínculo)
  const [editDireitoFerias, setEditDireitoFerias] = useState(true);
  // chaves externas editáveis (correção E10b) — strings no campo; "" = limpar (null)
  const [editMatricula, setEditMatricula] = useState("");
  const [editIdConvenia, setEditIdConvenia] = useState("");
  // modal nova vigência (aba Contrato)
  const [modalAberto, setModalAberto] = useState(false);
  // foto do cadastro (aba Dados)
  const [fotoPreview, setFotoPreview] = useState<string | null>(null);
  const [fotoArquivo, setFotoArquivo] = useState<File | null>(null);
  const [erroFoto, setErroFoto] = useState<string | null>(null);
  const [salvandoFoto, setSalvandoFoto] = useState(false);
  const [recarregaFoto, setRecarregaFoto] = useState(0);
  const [vig, setVig] = useState<number | null>(null);
  const [salario, setSalario] = useState<number | null>(null);
  const [bonus, setBonus] = useState<number | null>(null);
  // 05/09/2026 — isento de ponto na nova vigência (default herdado da atual)
  const [isentoPonto, setIsentoPonto] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [erroModal, setErroModal] = useState<string | null>(null);
  // 1ª vigência a partir do Convenia (sem contrato anterior para herdar — pacote 30/07/2026)
  const [jornadas, setJornadas] = useState<Jornada[]>([]);
  const [jornadaId, setJornadaId] = useState<string | null>(null);
  const [admissao, setAdmissao] = useState<string | null>(null);
  const [cargo, setCargo] = useState("");
  const [propostaAlertas, setPropostaAlertas] = useState<string[]>([]);
  const [preenchendo, setPreenchendo] = useState(false);
  // P2 — avisos de carga histórica da última vigência criada (meses fechados cobertos)
  const [avisosVigencia, setAvisosVigencia] = useState<string[]>([]);

  async function carregar() {
    try {
      setErro(null);
      const d = await api.get<Detalhe>(`/funcionarios/${id}`);
      setF(d);
      setEditNome(d.nome);
      setEditCpf(d.cpf ?? "");
      setEditAtivo(d.ativo);
      setEditObs(d.observacao ?? "");
      setEditDireitoFerias(d.direitoFerias ?? d.tipoVinculo !== "socio");
      setEditMatricula(d.matriculaDominio != null ? String(d.matriculaDominio) : "");
      setEditIdConvenia(d.idConvenia != null ? String(d.idConvenia) : "");
    } catch (e) {
      setErro(e);
    }
  }
  useEffect(() => {
    void carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  /** Selecionou arquivo: valida no cliente (tipo/tamanho) e mostra o preview. */
  function selecionarFoto(arquivo: File | null) {
    setErroFoto(null);
    setFotoArquivo(null);
    setFotoPreview((antiga) => {
      if (antiga) URL.revokeObjectURL(antiga);
      return null;
    });
    if (!arquivo) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(arquivo.type)) {
      setErroFoto(`Tipo de imagem não suportado ("${arquivo.type || "desconhecido"}") — use JPEG, PNG ou WebP.`);
      return;
    }
    if (arquivo.size > 2 * 1024 * 1024) {
      setErroFoto(`Foto com ${(arquivo.size / 1024 / 1024).toFixed(1)} MB — o limite é 2 MB. Exporte uma versão menor.`);
      return;
    }
    setFotoArquivo(arquivo);
    setFotoPreview(URL.createObjectURL(arquivo)); // preview redimensionado pelo navegador (só exibição)
  }

  async function salvarFoto() {
    if (!fotoArquivo || !f) return;
    setSalvandoFoto(true);
    setErroFoto(null);
    try {
      const base64 = await new Promise<string>((resolve, reject) => {
        const leitor = new FileReader();
        leitor.onload = () => resolve(String(leitor.result).split(",")[1]!);
        leitor.onerror = reject;
        leitor.readAsDataURL(fotoArquivo);
      });
      await api.put(`/funcionarios/${id}/foto`, { conteudoBase64: base64, contentType: fotoArquivo.type });
      invalidarCacheFoto(id);
      setFotoArquivo(null);
      setFotoPreview((antiga) => {
        if (antiga) URL.revokeObjectURL(antiga);
        return null;
      });
      notificarOk("Foto atualizada — já aparece na listagem.");
      setRecarregaFoto((x) => x + 1);
    } catch (e) {
      setErroFoto(e instanceof Error ? e.message : String(e));
      notificarErroApi(e);
    } finally {
      setSalvandoFoto(false);
    }
  }

  async function removerFoto() {
    if (!f) return;
    try {
      await api.del(`/funcionarios/${id}/foto`);
      invalidarCacheFoto(id);
      notificarOk("Foto removida — voltaram as iniciais coloridas.");
      setRecarregaFoto((x) => x + 1);
    } catch (e) {
      notificarErroApi(e);
    }
  }

  async function salvarDados() {
    try {
      setErro(null);
      await api.put(`/funcionarios/${id}`, {
        nome: editNome,
        cpf: editCpf || null,
        ativo: editAtivo,
        observacao: editObs || null,
        direitoFerias: editDireitoFerias,
        matriculaDominio: editMatricula === "" ? null : Number(editMatricula),
        idConvenia: editIdConvenia === "" ? null : Number(editIdConvenia),
      });
      notificarOk("Dados atualizados.");
      await carregar();
    } catch (e) {
      setErro(e);
      notificarErroApi(e);
    }
  }

  const vigente = f?.contratos[0];

  function abrirModalVigencia() {
    setSalario(vigente?.salarioBaseCentavos ?? null);
    setBonus(vigente?.bonusFixoMensalCentavos ?? null);
    setVig(null);
    setMotivo("");
    setErroModal(null);
    setIsentoPonto(vigente?.isentoPonto ?? false);
    // 03/09/2026 (pedido do dono) — a JORNADA também muda por quebra (igual aos
    // benefícios): "a partir de tal mês trabalha 200h, não mais 220h". O seletor
    // passa a existir na nova vigência (default = a do contrato atual); antes só
    // havia na 1ª vigência e a quebra herdava fixo. O histórico já mostra a
    // jornada por vigência (timeline da aba Contrato).
    setJornadaId(vigente?.jornada.id ?? null);
    setAdmissao(null);
    setCargo("");
    setPropostaAlertas([]);
    setModalAberto(true);
    // as jornadas são necessárias nos DOIS caminhos (quebra também tem seletor)
    if (jornadas.length === 0) {
      api
        .get<{ jornadas: Jornada[] }>("/jornadas")
        .then((r) => setJornadas(r.jornadas))
        .catch(() => undefined);
    }
    if (!vigente) {
      // 1ª vigência: sem contrato para herdar — já tenta o preenchimento
      // automático a partir do Convenia (salário, jornada e admissão do espelho)
      void preencherDoConvenia(null);
    }
  }

  /** Pré-preenche a 1ª vigência com a proposta derivada do Convenia. Campos
   *  continuam editáveis; bloqueios da proposta aparecem como alerta. */
  async function preencherDoConvenia(competencia: number | null) {
    setPreenchendo(true);
    setErroModal(null);
    try {
      const q = competencia ? `?competencia=${competencia}` : "";
      const p = await api.get<PropostaPrimeiraVigencia>(`/funcionarios/${id}/proposta-primeira-vigencia${q}`);
      if (p.salarioBaseCentavos !== null) setSalario(p.salarioBaseCentavos);
      if (p.jornadaId) setJornadaId(p.jornadaId);
      if (p.admissao) setAdmissao(p.admissao);
      if (p.cargo) setCargo(p.cargo);
      setPropostaAlertas([...p.alertas, ...p.bloqueios]);
    } catch (e) {
      setErroModal(e instanceof ErroApi ? String((e.corpo as { erro?: string })?.erro ?? e.message) : String(e));
    } finally {
      setPreenchendo(false);
    }
  }

  /** Cria a 1ª vigência contratual a partir do Convenia (com os ajustes feitos
   *  na tela — a proposta é o ponto de partida, não uma imposição). */
  async function criarPrimeiraVigencia() {
    if (!f || vigente || vig === null) return;
    try {
      setErroModal(null);
      const resposta = await api.post(`/funcionarios/${id}/contratos/primeira-do-convenia`, {
        competencia: vig,
        salarioBaseCentavos: salario ?? undefined,
        jornadaId: jornadaId ?? undefined,
        admissao: admissao ?? undefined,
        cargo: cargo.trim() || undefined,
        motivo: motivo || undefined,
      });
      setModalAberto(false);
      setAvisosVigencia(avisosDe(resposta));
      setOk("1ª vigência criada a partir do Convenia — a pessoa já entra na projeção da folha.");
      notificarOk("1ª vigência criada a partir do Convenia.");
      await carregar();
    } catch (e) {
      setErroModal(e instanceof ErroApi ? String((e.corpo as { erro?: string })?.erro ?? e.message) : String(e));
      notificarErroApi(e);
    }
  }

  /** Diff antes → depois do modal de nova vigência. */
  function diffVigencia(): string[] {
    if (!vigente) return [];
    const partes: string[] = [];
    if (salario !== null && salario !== vigente.salarioBaseCentavos) {
      partes.push(`Salário: ${fmtMoeda(vigente.salarioBaseCentavos)} → ${fmtMoeda(salario)}`);
    }
    if (bonus !== null && bonus !== (vigente.bonusFixoMensalCentavos ?? 0)) {
      partes.push(`Bônus fixo: ${fmtMoeda(vigente.bonusFixoMensalCentavos ?? 0)} → ${fmtMoeda(bonus)}`);
    }
    if (vig !== null) {
      partes.push(`Vigência: passa a valer de ${fmtCompetencia(vig)} (a atual fecha em ${fmtCompetencia(vig - (vig % 100 === 1 ? 89 : 1))})`);
    }
    // 03/09/2026 — a jornada pode mudar na quebra (temporal, com histórico)
    if (jornadaId && jornadaId !== vigente.jornada.id) {
      const nova = jornadas.find((j) => j.id === jornadaId);
      partes.push(`Jornada: ${vigente.jornada.descricao} → ${nova ? `${nova.descricao} (divisor ${nova.divisorHorasMes})` : jornadaId}`);
    }
    // 05/09/2026 — a isenção de ponto também muda por quebra
    if (isentoPonto !== (vigente.isentoPonto ?? false)) {
      partes.push(
        isentoPonto
          ? "Isenção de ponto: passa a ser ISENTO (cargo de confiança — sem média de HE nos recibos)"
          : "Isenção de ponto: volta a bater ponto (a média de HE volta a valer)",
      );
    }
    if (motivo.trim()) partes.push(`Motivo: ${motivo.trim()}`);
    return partes;
  }

  async function criarVigencia() {
    if (!f || !vigente) return;
    try {
      setErroModal(null);
      const resposta = await api.post(`/funcionarios/${id}/contratos`, {
        vigenciaInicio: vig,
        salarioBaseCentavos: salario,
        jornadaId: jornadaId ?? vigente.jornada.id,
        admissao: vigente.admissao.slice(0, 10),
        cargo: vigente.cargo ?? undefined,
        cbo: vigente.cbo ?? undefined,
        departamentoDominio: vigente.departamentoDominio ?? undefined,
        situacao: vigente.situacao,
        motivo: motivo || undefined,
        bonusFixoMensalCentavos: bonus ?? undefined,
        isentoPonto,
      });
      setModalAberto(false);
      // P2 — carga histórica: a vigência pode cobrir meses fechados (permitido
      // com aviso — os snapshots dos fechados não mudam)
      setAvisosVigencia(avisosDe(resposta));
      setOk("Nova vigência criada — a anterior foi fechada na competência anterior.");
      notificarOk("Nova vigência criada.");
      await carregar();
    } catch (e) {
      setErroModal(e instanceof ErroApi ? String((e.corpo as { erro?: string })?.erro ?? e.message) : String(e));
      notificarErroApi(e);
    }
  }

  if (!f) {
    return (
      <>
        <Title order={2}>Funcionário</Title>
        <ErroApiAlert erro={erro} />
        <Text c="dimmed">Carregando…</Text>
      </>
    );
  }

  return (
    <>
      <Group justify="space-between" mb="sm" wrap="wrap">
        <Title order={2}>{f.nome}</Title>
        <Badge size="lg" color={f.ativo ? "sillis" : "gray"}>
          {f.tipoVinculo === "socio" ? "sócio" : f.ativo ? "ativo" : "inativo"}
        </Badge>
      </Group>
      <ErroApiAlert erro={erro} />
      <OkAlert>{ok}</OkAlert>
      <AvisosAlert avisos={avisosVigencia} />

      <Tabs defaultValue="dados" keepMounted={false}>
        <Tabs.List mb="md">
          <Tabs.Tab value="dados">Dados</Tabs.Tab>
          <Tabs.Tab value="contrato">Contrato</Tabs.Tab>
          <Tabs.Tab value="historico-salarial">Histórico salarial</Tabs.Tab>
          <Tabs.Tab value="medias">Médias</Tabs.Tab>
          <Tabs.Tab value="beneficios">Benefícios</Tabs.Tab>
          <Tabs.Tab value="dependentes">Dependentes</Tabs.Tab>
          <Tabs.Tab value="banco">Banco de horas</Tabs.Tab>
          <Tabs.Tab value="documentos">Documentos</Tabs.Tab>
          <Tabs.Tab value="bancario">Bancário</Tabs.Tab>
          <Tabs.Tab value="emergencia-aso">Emergência & ASO</Tabs.Tab>
          <Tabs.Tab value="ferias-afastamentos">Férias & afastamentos</Tabs.Tab>
          <Tabs.Tab value="anotacoes-campos">Anotações & campos</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="dados">
          <Card withBorder shadow="xs" maw={560} mb="md">
            <Title order={5} mb="sm">Foto do cadastro</Title>
            <Group align="flex-start" wrap="nowrap">
              {fotoPreview ? (
                <Avatar src={fotoPreview} size={80} radius="md" alt="Preview da nova foto" />
              ) : (
                <div key={recarregaFoto}>
                  <AvatarPessoa funcionarioId={id} nome={f.nome} size={80} radius="md" />
                </div>
              )}
              <div>
                <Group gap="sm" mb="xs">
                  <FileButton onChange={selecionarFoto} accept="image/jpeg,image/png,image/webp">
                    {(props) => (
                      <Button {...props} variant="light" leftSection={<IconCamera size={16} />}>
                        Escolher foto…
                      </Button>
                    )}
                  </FileButton>
                  {fotoArquivo && (
                    <Button onClick={() => void salvarFoto()} loading={salvandoFoto}>
                      Salvar foto
                    </Button>
                  )}
                  <Button variant="subtle" color="red" leftSection={<IconTrash size={14} />} onClick={() => void removerFoto()}>
                    Remover
                  </Button>
                </Group>
                <Text size="xs" c="dimmed">
                  JPEG, PNG ou WebP, até 2 MB. Sem foto, o avatar mostra as iniciais coloridas (a foto vale para a listagem e o detalhe).
                </Text>
                {erroFoto && (
                  <Text size="sm" c="red.7" mt={4} role="alert">
                    {erroFoto}
                  </Text>
                )}
              </div>
            </Group>
          </Card>
          <Card withBorder shadow="xs" maw={560}>
            <TextInput label="Nome oficial" value={editNome} onChange={(e) => setEditNome(e.currentTarget.value.toUpperCase())} mb="sm" />
            <Group grow mb="sm">
              <TextInput
                label="Matrícula Domínio"
                description="Chave dos exports/conferência com a contabilidade (vazio = pendente)"
                value={editMatricula}
                onChange={(e) => setEditMatricula(e.currentTarget.value.replace(/\D/g, ""))}
                inputMode="numeric"
              />
              <TextInput
                label="ID Convenia (numérico)"
                description="Chave do fluxo xlsx de eventos (vazio = pendente)"
                value={editIdConvenia}
                onChange={(e) => setEditIdConvenia(e.currentTarget.value.replace(/\D/g, ""))}
                inputMode="numeric"
              />
            </Group>
            <TextInput
              label="UUID Convenia (chave do sync)"
              description="Vínculo com a API do Convenia — preenchido pelo sync, não editável"
              value={f.conveniaUuid ?? "não vinculado — rode o sync Convenia"}
              readOnly
              variant="filled"
              mb="sm"
            />
            <TextInput
              label="CPF"
              value={editCpf}
              onChange={(e) => setEditCpf(e.currentTarget.value.replace(/\D/g, "").slice(0, 11))}
              inputMode="numeric"
              mb="sm"
            />
            <TextInput label="Observações" value={editObs} onChange={(e) => setEditObs(e.currentTarget.value)} mb="sm" />
            <Switch label="Ativo" description="Inativo tira a pessoa da projeção da folha." checked={editAtivo} onChange={(e) => setEditAtivo(e.currentTarget.checked)} mb="md" />
            <Switch
              label="Permitir direito a férias"
              description="Desligado (padrão dos sócios): a pessoa sai das listas e alertas de férias — a aba 'Férias & afastamentos' fica só informativa, sem o vermelho de vencido."
              checked={editDireitoFerias}
              onChange={(e) => setEditDireitoFerias(e.currentTarget.checked)}
              mb="md"
            />
            <Button onClick={() => void salvarDados()}>Salvar dados</Button>
          </Card>
        </Tabs.Panel>

        <Tabs.Panel value="contrato">
          <DesligamentoCard funcionarioId={id} />
          <Group justify="space-between" mb="sm">
            <Title order={4}>Histórico de vigências</Title>
            {(f.tipoVinculo === "clt" || f.tipoVinculo === "socio") && (
              <Button leftSection={<IconPlus size={16} />} onClick={abrirModalVigencia}>
                {vigente ? "Nova vigência" : "Criar 1ª vigência (do Convenia)"}
              </Button>
            )}
          </Group>
          <Timeline active={0} bulletSize={22} lineWidth={2}>
            {f.contratos.map((c, i) => (
              <Timeline.Item
                key={c.id}
                title={
                  <Text fw={600}>
                    {fmtCompetencia(c.vigenciaInicio)} → {c.vigenciaFim ? fmtCompetencia(c.vigenciaFim) : "em vigor"}
                    {i === 0 && (
                      <Badge ml="sm" size="sm" color="sillis">
                        atual
                      </Badge>
                    )}
                    {c.isentoPonto === true && (
                      <Badge ml="sm" size="sm" color="grape" variant="light">
                        isento de ponto (cargo de confiança — sem HE)
                      </Badge>
                    )}
                  </Text>
                }
              >
                <Text size="sm">
                  <strong>{fmtMoeda(c.salarioBaseCentavos)}</strong>
                  {c.bonusFixoMensalCentavos > 0 ? ` + ${fmtMoeda(c.bonusFixoMensalCentavos)} bônus` : ""} · {c.cargo ?? "sem cargo"}
                </Text>
                <Text size="sm" c="dimmed">
                  {c.jornada.descricao} · {c.situacao === "suspenso_inss" ? "suspenso (INSS)" : c.situacao} · admissão {fmtData(c.admissao)}
                  {c.cbo ? ` · CBO ${c.cbo}` : ""}
                </Text>
              </Timeline.Item>
            ))}
          </Timeline>
        </Tabs.Panel>

        <Tabs.Panel value="historico-salarial">
          <HistoricoSalarialTab funcionarioId={id} />
        </Tabs.Panel>

        <Tabs.Panel value="medias">
          <MediasTab funcionarioId={id} />
        </Tabs.Panel>

        <Tabs.Panel value="beneficios">
          <BeneficiosTab funcionarioId={id} />
        </Tabs.Panel>

        <Tabs.Panel value="dependentes">
          <DependentesTab funcionarioId={id} quantidadeManual={f.dependentes[0]?.quantidade ?? 0} />
        </Tabs.Panel>

        <Tabs.Panel value="banco">
          <BancoHorasPage funcionarioId={id} />
        </Tabs.Panel>

        <Tabs.Panel value="documentos">
          <DocumentosTab funcionarioId={id} />
        </Tabs.Panel>

        <Tabs.Panel value="bancario">
          <BancarioTab funcionarioId={id} />
        </Tabs.Panel>

        <Tabs.Panel value="emergencia-aso">
          <EmergenciaAsoTab funcionarioId={id} />
        </Tabs.Panel>

        <Tabs.Panel value="ferias-afastamentos">
          <FeriasAfastamentosTab funcionarioId={id} />
        </Tabs.Panel>

        <Tabs.Panel value="anotacoes-campos">
          <AnotacoesCamposTab funcionarioId={id} />
        </Tabs.Panel>
      </Tabs>

      {/* Modal guiado de vigência contratual: com contrato vigente = nova
          vigência herdada com diff antes→depois; sem contrato = 1ª vigência a
          partir do Convenia (pacote 30/07/2026) */}
      <Modal
        opened={modalAberto}
        onClose={() => setModalAberto(false)}
        title={vigente ? `Nova vigência contratual — ${f.nome.split(" ")[0]}` : `1ª vigência contratual — ${f.nome.split(" ")[0]}`}
        centered
      >
        {vigente ? (
          <>
            <Text size="sm" c="dimmed" mb="sm">
              Herda do contrato atual: {vigente.cargo ?? "sem cargo"}, {vigente.jornada.descricao}, {vigente.situacao}, admissão{" "}
              {fmtData(vigente.admissao)}. A vigência atual é fechada na competência anterior à nova.
            </Text>
            <Group grow mb="sm">
              <div>
                <Text size="sm" fw={600} mb={2}>Vale a partir de</Text>
                <CompetenciaField valor={vig} onChange={setVig} />
              </div>
              <MoedaField label="Salário" valor={salario} onChange={setSalario} />
            </Group>
            <Group grow mb="sm">
              <MoedaField label="Bônus fixo mensal (integra 13º/férias)" valor={bonus} onChange={setBonus} />
              {/* 03/09/2026 — quebra de jornada (a partir de MM/AAAA) */}
              <Select
                label="Jornada (total de horas/mês)"
                description="Mudar a jornada cria a quebra: a nova vale do mês escolhido em diante; o histórico fica na timeline"
                data={jornadas.map((j) => ({ value: j.id, label: `${j.descricao} (divisor ${j.divisorHorasMes})` }))}
                value={jornadaId}
                onChange={setJornadaId}
              />
            </Group>
            <TextInput label="Motivo (opcional — ex.: dissídio, mérito)" value={motivo} onChange={(e) => setMotivo(e.currentTarget.value)} mb="sm" />
            {/* 05/09/2026 — isento de ponto (cargo de confiança): quebra como a
                jornada; o default é herdado da vigência atual */}
            <Checkbox
              label="Isento de ponto (cargo de confiança — sem média de HE)"
              description="Sem registro de ponto (CLT art. 62): não acumula horas extras — a média de HORAS dos recibos de férias zera; a de valores segue."
              checked={isentoPonto}
              onChange={(e) => setIsentoPonto(e.currentTarget.checked)}
              mb="sm"
            />
            {diffVigencia().length > 0 && (
              <Card withBorder mb="sm" bg="gray.0">
                <Text size="sm" fw={700} mb={2}>
                  O que muda (antes → depois):
                </Text>
                {diffVigencia().map((d, i) => (
                  <Text key={i} size="sm">
                    • {d}
                  </Text>
                ))}
              </Card>
            )}
            {erroModal && (
              <Text size="sm" c="red.7" mb="sm" role="alert">
                {erroModal}
              </Text>
            )}
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setModalAberto(false)}>
                Voltar
              </Button>
              <Button onClick={() => void criarVigencia()} disabled={vig === null || salario === null}>
                Criar vigência
              </Button>
            </Group>
          </>
        ) : (
          <>
            <Text size="sm" c="dimmed" mb="sm">
              Sem contrato anterior para herdar — preencha do Convenia (salário vigente no histórico, jornada e admissão do
              espelho) e ajuste o que precisar antes de gravar.
            </Text>
            <Group grow mb="sm">
              <div>
                <Text size="sm" fw={600} mb={2}>Vale a partir de</Text>
                <CompetenciaField valor={vig} onChange={setVig} />
              </div>
              <MoedaField label="Salário" valor={salario} onChange={setSalario} required />
            </Group>
            <Group grow mb="sm">
              <Select
                label="Jornada"
                placeholder="Selecione"
                data={jornadas.map((j) => ({ value: j.id, label: `${j.descricao} (divisor ${j.divisorHorasMes})` }))}
                value={jornadaId}
                onChange={setJornadaId}
              />
              <DataField label="Admissão" valor={admissao} onChange={setAdmissao} />
            </Group>
            <TextInput label="Cargo" value={cargo} onChange={(e) => setCargo(e.currentTarget.value.toUpperCase())} mb="sm" />
            <TextInput label="Motivo (opcional)" value={motivo} onChange={(e) => setMotivo(e.currentTarget.value)} mb="sm" />
            <Group mb="sm">
              <Button variant="light" size="compact-sm" loading={preenchendo} onClick={() => void preencherDoConvenia(vig)}>
                Preencher do Convenia{vig ? ` (${fmtCompetencia(vig)})` : ""}
              </Button>
            </Group>
            {propostaAlertas.length > 0 && (
              <Card withBorder mb="sm" bg="yellow.0">
                {propostaAlertas.map((a, i) => (
                  <Text key={i} size="sm">
                    ⚠ {a}
                  </Text>
                ))}
              </Card>
            )}
            {erroModal && (
              <Text size="sm" c="red.7" mb="sm" role="alert">
                {erroModal}
              </Text>
            )}
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setModalAberto(false)}>
                Voltar
              </Button>
              <Button
                onClick={() => void criarPrimeiraVigencia()}
                disabled={vig === null || salario === null || jornadaId === null || admissao === null}
              >
                Criar 1ª vigência
              </Button>
            </Group>
          </>
        )}
      </Modal>
    </>
  );
}
